import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, Bug, Check, Phone, Clock } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const pestBasePrices = {
  house: { standard: { "1": 165, "2": 175, "3": 198, "4": 220, "5": 250, "6": 285 }, comprehensive: { "1": 220, "2": 240, "3": 280, "4": 330, "5": 380, "6": 450 } },
  unit: { standard: { "1": 150, "2": 170, "3": 190, "4": 210, "5": 240, "6": 270 }, comprehensive: { "1": 195, "2": 220, "3": 250, "4": 290, "5": 330, "6": 380 } }
};

export default function PestOnlyCalculator({ source = "landing_page" }) {
  const [propertyType, setPropertyType] = useState("house");
  const [bedrooms, setBedrooms] = useState("3");
  const [storeys, setStoreys] = useState("single");
  const [serviceFrequency, setServiceFrequency] = useState("oneoff");
  const [treatmentType, setTreatmentType] = useState("standard");
  const [rodentTreatment, setRodentTreatment] = useState(false);
  const [addTermite, setAddTermite] = useState(false);

  const [formData, setFormData] = useState({ name: "", phone: "", email: "", address: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Pest pricing
  let pestPrice = pestBasePrices[propertyType]?.[treatmentType]?.[bedrooms] || 198;
  if (storeys === "double") pestPrice += 30;
  if (serviceFrequency === "sixmonthly") pestPrice = Math.round(pestPrice * 0.80);

  let rodentPrice = 0;
  const rodentDisplayPrice = (treatmentType === "comprehensive" ? 120 : 85) + (storeys === "double" ? 20 : 0);
  if (rodentTreatment) rodentPrice = rodentDisplayPrice;

  // Termite pricing
  let termitePrice = 0;
  if (addTermite) {
    termitePrice = 150;
    const bedroomNum = parseInt(bedrooms);
    if (bedroomNum >= 5) termitePrice += 70;
    else if (bedroomNum >= 4) termitePrice += 40;
    else if (bedroomNum >= 3) termitePrice += 20;
    if (storeys === "double") termitePrice += 60;
  }

  const grandTotal = pestPrice + rodentPrice + termitePrice;
  const launchDiscount = Math.round(grandTotal * 0.10);
  const discountedTotal = grandTotal - launchDiscount;

  // Sticky mobile price bar - show only during calculator, hide at final price
  const priceBoxRef = useRef(null);
  const calcSectionRef = useRef(null);
  const [showStickyPrice, setShowStickyPrice] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const calc = calcSectionRef.current;
      const price = priceBoxRef.current;
      if (!calc) return;
      const calcRect = calc.getBoundingClientRect();
      const inCalc = calcRect.top < window.innerHeight && calcRect.bottom > 100;
      let priceVis = false;
      if (price) {
        const pr = price.getBoundingClientRect();
        priceVis = pr.top < window.innerHeight && pr.bottom > 0;
      }
      setShowStickyPrice(inCalc && !priceVis);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Refs for auto-scroll between questions
  const refBedrooms = useRef(null);
  const refStoreys = useRef(null);
  const refTreatment = useRef(null);
  const refFrequency = useRef(null);
  const refAddons = useRef(null);
  const scrollTo = (ref) => setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/pest-estimate`, {
        ...formData, propertyType, bedrooms, storeys, treatmentType,
        serviceFrequency, rodentTreatment,
        termiteAddOn: addTermite, termiteInspectionEstimate: termitePrice || null,
        pricePerVisit: discountedTotal, source
      });
      setSubmitted(true);
      toast.success("Quote request sent! We'll be in touch shortly.");
    } catch { toast.error("Failed to send. Please call us."); }
    finally { setSubmitting(false); }
  };

  const reset = () => {
    setPropertyType("house"); setBedrooms("3"); setStoreys("single");
    setServiceFrequency("oneoff"); setTreatmentType("standard");
    setRodentTreatment(false); setAddTermite(false);
    setFormData({ name: "", phone: "", email: "", address: "" }); setSubmitted(false);
  };

  return (
    <>
    <div ref={calcSectionRef} className="bg-white rounded-2xl shadow-xl overflow-hidden" data-testid="pest-calculator">
      <div className="bg-gradient-to-r from-[#E74C3C] to-[#c0392b] text-white p-4 flex items-center gap-3">
        <Shield className="w-6 h-6" />
        <h3 className="text-xl font-bold">Get Your Instant Pest Control Quote</h3>
      </div>

      <div className="grid md:grid-cols-2">
        {/* LEFT: Inputs */}
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-medium mb-1.5 block text-sm">Property type</Label>
              <div className="flex gap-1">
                {[["house", "House"], ["unit", "Unit"]].map(([val, label]) => (
                  <Button key={val} type="button" variant={propertyType === val ? "default" : "outline"} size="sm"
                    className={`flex-1 ${propertyType === val ? "bg-[#7ABC32] hover:bg-[#6AAD28]" : ""}`}
                    onClick={() => { setPropertyType(val); scrollTo(refBedrooms); }} data-testid={`property-${val}`}>{label}</Button>
                ))}
              </div>
            </div>
            <div ref={refBedrooms} className="scroll-mt-20">
              <Label className="font-medium mb-1.5 block text-sm">Bedrooms</Label>
              <div className="flex gap-1">
                {["1","2","3","4","5","6"].map(n => (
                  <Button key={n} type="button" variant={bedrooms === n ? "default" : "outline"} size="sm"
                    className={`flex-1 px-0 ${bedrooms === n ? "bg-[#7ABC32] hover:bg-[#6AAD28]" : ""}`}
                    onClick={() => { setBedrooms(n); scrollTo(refStoreys); }} data-testid={`bedrooms-${n}`}>{n}</Button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div ref={refStoreys} className="scroll-mt-20">
              <Label className="font-medium mb-1.5 block text-sm">Storeys</Label>
              <div className="flex gap-1">
                {[["single", "Single"], ["double", "Double"]].map(([val, label]) => (
                  <Button key={val} type="button" variant={storeys === val ? "default" : "outline"} size="sm"
                    className={`flex-1 ${storeys === val ? "bg-[#7ABC32] hover:bg-[#6AAD28]" : ""}`}
                    onClick={() => { setStoreys(val); scrollTo(refTreatment); }} data-testid={`storeys-${val}`}>{label}</Button>
                ))}
              </div>
            </div>
            <div ref={refTreatment} className="scroll-mt-20">
              <Label className="font-medium mb-1.5 block text-sm">Treatment</Label>
              <div className="flex gap-1">
                {[["standard", "Standard"], ["comprehensive", "Comprehensive"]].map(([val, label]) => (
                  <Button key={val} type="button" variant={treatmentType === val ? "default" : "outline"} size="sm"
                    className={`flex-1 ${treatmentType === val ? "bg-[#7ABC32] hover:bg-[#6AAD28]" : ""}`}
                    onClick={() => { setTreatmentType(val); scrollTo(refFrequency); }} data-testid={`treatment-${val}`}>{label}</Button>
                ))}
              </div>
            </div>
          </div>

          <div ref={refFrequency} className="scroll-mt-20">
            <Label className="font-medium mb-1.5 block text-sm">Frequency</Label>
            <div className="flex gap-1">
              {[["sixmonthly", "6-Monthly"], ["oneoff", "Yearly"]].map(([val, label]) => (
                <Button key={val} type="button" variant={serviceFrequency === val ? "default" : "outline"} size="sm"
                  className={`flex-1 ${serviceFrequency === val ? "bg-[#7ABC32] hover:bg-[#6AAD28]" : ""}`}
                  onClick={() => { setServiceFrequency(val); scrollTo(refAddons); }}>
                  {label} {val === "sixmonthly" && <span className="ml-1 text-[10px] bg-[#7ABC32] text-white px-1 rounded">20%</span>}
                </Button>
              ))}
            </div>
          </div>

          {/* Correct treatment info */}
          <div className="bg-[#E74C3C]/5 border border-[#E74C3C]/20 rounded-lg p-3">
            <p className="text-xs font-semibold text-[#E74C3C] mb-1.5">{treatmentType === "standard" ? "Standard" : "Comprehensive"} includes:</p>
            <ul className="space-y-1 text-xs text-slate-600">
              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-slate-400 flex-shrink-0" />Internal treatment</li>
              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-slate-400 flex-shrink-0" />External treatment</li>
              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-slate-400 flex-shrink-0" />Harbourage areas (fridge, dishwasher, sinks, subfloor)</li>
              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-slate-400 flex-shrink-0" />Cockroach gel bait application</li>
              {treatmentType === "comprehensive" && (
                <>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Fence / full perimeter treatment</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Roof void dusting</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />3x cockroach pheromone sticky traps</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Ant treatment included</li>
                </>
              )}
              <li className="flex items-center gap-1.5"><Check className={`w-3 h-3 flex-shrink-0 ${treatmentType === "comprehensive" ? "text-[#7ABC32]" : "text-[#E74C3C]"}`} /><span className={treatmentType === "comprehensive" ? "text-[#7ABC32] font-medium" : "text-[#E74C3C] font-medium"}>{treatmentType === "standard" ? "3-month warranty" : "6-month warranty"}</span></li>
            </ul>
          </div>

          {/* Rodent */}
          <div ref={refAddons} className="scroll-mt-20">
          <label className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg cursor-pointer border-2 border-slate-200">
            <Checkbox checked={rodentTreatment} onCheckedChange={setRodentTreatment} className="mt-0.5" />
            <div>
              <span className="font-medium text-sm">+ Rodent Treatment (+${rodentDisplayPrice})</span>
              {rodentTreatment && (
                <ul className="mt-1.5 space-y-0.5 text-xs text-slate-600">
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />Internal & external bait stations</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />Entry point identification</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />Ongoing monitoring program</li>
                </ul>
              )}
            </div>
          </label>

          {/* Termite */}
          <label className={`flex items-start gap-2 p-3 rounded-lg cursor-pointer border-2 transition-colors ${addTermite ? 'bg-[#D2691E]/10 border-[#D2691E]' : 'bg-slate-50 border-slate-200'}`}>
            <Checkbox checked={addTermite} onCheckedChange={setAddTermite} className="mt-0.5" />
            <div>
              <span className="font-bold text-sm flex items-center gap-1.5">
                <Bug className="w-4 h-4 text-[#D2691E]" /> Add Termite Inspection (+${termitePrice || 150})
              </span>
              {addTermite && (
                <ul className="mt-1.5 space-y-0.5 text-xs text-slate-600">
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#D2691E] flex-shrink-0" />Full property termite inspection</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#D2691E] flex-shrink-0" />Thermal imaging & moisture detection</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#D2691E] flex-shrink-0" />Detailed report with photos</li>
                </ul>
              )}
            </div>
          </label>
          </div>
        </div>

        {/* RIGHT: Price & Form */}
        <div className="bg-slate-50 p-5 border-t md:border-t-0 md:border-l">
          {!submitted ? (
            <>
              <div ref={priceBoxRef} className="rounded-xl overflow-hidden mb-4 bg-[#E74C3C]">
                <div className="bg-[#c0392b] px-4 py-2 flex items-center justify-between">
                  <img src="https://customer-assets.emergentagent.com/job_1dcfe10c-2a6c-4f88-b8f3-7699155d0720/artifacts/9wru6aby_IMG_1548.png" alt="Pest Free" className="h-10" />
                  <span className="text-amber-400 text-xs font-bold whitespace-nowrap">10% OFF LAUNCH SPECIAL</span>
                </div>
                <div className="p-4 text-white text-center">
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-lg text-white/50 line-through">${grandTotal}</span>
                    <span className="text-3xl font-bold">${discountedTotal}</span>
                    <span className="text-sm text-white/70">per visit</span>
                  </div>
                  <p className="text-amber-400 font-semibold text-sm">Save ${launchDiscount} (10% off)</p>
                  <p className="text-xs text-yellow-300 font-semibold mt-1 flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> Offer ends March 31</p>
                  {serviceFrequency === "sixmonthly" && <p className="text-xs text-white/70 mt-1">Per visit (6-monthly plan)</p>}
                  <div className="mt-3 text-left text-xs space-y-1 border-t border-white/20 pt-2">
                    <div className="flex justify-between"><span>Pest control ({treatmentType}{serviceFrequency === "sixmonthly" ? ", 6-mo" : ""})</span><span>${pestPrice}</span></div>
                    {rodentTreatment && <div className="flex justify-between"><span>Rodent treatment</span><span>${rodentPrice}</span></div>}
                    {addTermite && <div className="flex justify-between"><span>Termite inspection</span><span>${termitePrice}</span></div>}
                    <div className="text-white/50 text-[10px] pt-1 border-t border-white/10">Prices include GST</div>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Name *" className="bg-white" data-testid="name-input" />
                  <Input required type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="Phone *" className="bg-white" data-testid="phone-input" />
                </div>
                <Input required type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="Email *" className="bg-white" data-testid="email-input" />
                <Textarea required value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Full address *" rows={2} className="bg-white" data-testid="address-input" />
                <Button type="submit" disabled={submitting} className="w-full bg-[#7ABC32] hover:bg-[#6AAD28] text-white py-5 font-semibold" data-testid="submit-btn">
                  {submitting ? "Sending..." : "Get My Quote Now"}
                </Button>
                <p className="text-center text-xs text-slate-500">Free quote in 60 seconds — no obligation</p>
              </form>

              <div className="mt-4 text-center p-3 bg-white rounded-lg border border-slate-200">
                <p className="text-xs text-slate-500 mb-1">Prefer to talk?</p>
                <a href="tel:0737081422" className="flex items-center justify-center gap-2 text-[#7ABC32] font-bold text-lg hover:underline">
                  <Phone className="w-5 h-5" /> (07) 3708 1422
                </a>
              </div>
            </>
          ) : (
            <div className="text-center py-12" data-testid="success-message">
              <div className="w-16 h-16 bg-[#7ABC32] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-2">Quote Sent!</h3>
              <p className="text-slate-600 mb-2">We'll be in touch within the hour.</p>
              <p className="text-2xl font-bold text-[#E74C3C] mb-4">${discountedTotal}</p>
              <Button onClick={reset} variant="outline" size="sm">Get Another Quote</Button>
              <div className="mt-6 p-3 bg-slate-100 rounded-lg">
                <p className="text-sm text-slate-600">Need it sooner?</p>
                <a href="tel:0737081422" className="text-[#7ABC32] font-bold text-lg flex items-center justify-center gap-2 mt-1">
                  <Phone className="w-5 h-5" /> (07) 3708 1422
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Sticky mobile price bar - only during calculator section */}
    {showStickyPrice && (
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-[#E74C3C] text-white px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.15)]">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div>
            <span className="text-xs text-white/70">Your estimate</span>
            <div className="flex items-baseline gap-2">
              <span className="text-sm text-white/50 line-through">${grandTotal}</span>
              <span className="text-xl font-bold">${discountedTotal}</span>
              <span className="text-xs text-white/60">/visit</span>
            </div>
          </div>
          <span className="text-xs font-semibold text-amber-300 bg-white/10 px-2 py-1 rounded-full">10% off</span>
        </div>
      </div>
    )}
    </>
  );
}
